/**
 * Project domain hooks (CashSync). Same pattern as `use-finance-query`:
 * the factory's canonical queryKey + encapsulated Drizzle query builders.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@cashsync/powersync";

import {
  getProjectDetailQuery,
  getProjectsQuery,
  getProjectTotalsQuery,
  type ProjectTotalsRow,
  type ProjectWithMeta,
  queryKeys,
} from "../index";

import { createPowerSyncQuery, DEFAULT_THROTTLE_MS } from "./create-power-sync-query";

interface BaseProjectsQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

/** Live projects for the user. */
export function useProjectsQuery(options: BaseProjectsQueryOptions) {
  return createPowerSyncQuery<ProjectWithMeta>({
    queryKey: () => queryKeys.projects.list(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getProjectsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** Aggregated totals (expense/income in base currency) per project. */
export function useProjectTotalsQuery(options: BaseProjectsQueryOptions) {
  return createPowerSyncQuery<ProjectTotalsRow>({
    queryKey: () => queryKeys.projects.totals(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getProjectTotalsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

interface UseProjectDetailQueryOptions extends BaseProjectsQueryOptions {
  projectId: Accessor<string | undefined>;
}

/** Project detail (single row in `data[0]`). */
export function useProjectDetailQuery(options: UseProjectDetailQueryOptions) {
  return createPowerSyncQuery<ProjectWithMeta>({
    queryKey: () => queryKeys.projects.detail(options.projectId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const id = options.projectId();
      return db && id ? getProjectDetailQuery(db, id) : null;
    },
    enabled: () => Boolean(options.projectId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}
